
import { Routes, Route, Link } from 'react-router-dom';
import { LayoutDashboard, Users, Car, Calendar, Settings as SettingsIcon, Bell, FileText, LogOut } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DashboardHome from './components/DashboardHome';
import Bookings from './components/Bookings';
import Vehicles from './components/Vehicles';
import Customers from './components/Customers';
import SettingsPage from './components/SettingsPage';
import ManageVehicle from './ManageVehicle';
import AdminAuth from './components/AdminAuth';
import AdminBlog from './components/AdminBlog';
import BlogEditor from './components/BlogEditor';
import NotificationsPage from './components/NotificationsPage';
import { supabase } from '../../integrations/supabase/client';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  read: boolean;
  created_at: string;
}

const AdminDashboard = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const queryClient = useQueryClient();
  const { signOut } = useAuth();
  
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['adminNotifications'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (error) {
        console.error('Error fetching notifications:', error);
        return [];
      }
      
      return data as Notification[];
    }
  });

  const markAsRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotifications'] });
    }
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead.mutate(notification.id);
    }
  };

  const handleLogout = () => {
    signOut();
  };

  const unreadCount = notifications?.filter(n => !n.read).length ?? 0;

  return (
    <AdminAuth>
      <div className="min-h-screen bg-gray-100">
        <div className="fixed inset-y-0 left-0 w-64 bg-gray-900 text-white">
          <div className="p-6">
            <h1 className="text-2xl font-bold">Admin Panel</h1>
          </div>
          <nav className="mt-6">
            <Link to="/admin" className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800">
              <LayoutDashboard className="w-5 h-5 mr-3" />
              Dashboard
            </Link>
            <Link to="/admin/bookings" className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800">
              <Calendar className="w-5 h-5 mr-3" />
              Bookings
            </Link>
            <Link to="/admin/blog" className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800">
              <FileText className="w-5 h-5 mr-3" />
              Blog Posts
            </Link>
            <Link to="/admin/vehicles" className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800">
              <Car className="w-5 h-5 mr-3" />
              Vehicles
            </Link>
            <Link to="/admin/customers" className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800">
              <Users className="w-5 h-5 mr-3" />
              Customers
            </Link>
            <Link to="/admin/notifications" className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800">
              <Bell className="w-5 h-5 mr-3" />
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Link>
            <Link to="/admin/settings" className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800">
              <SettingsIcon className="w-5 h-5 mr-3" />
              Settings
            </Link>
          </nav>
          <div className="absolute bottom-0 w-full p-6">
            <button
              onClick={handleLogout}
              className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800 w-full"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
        <div className="ml-64">
          <div className="bg-white shadow-sm">
            <div className="px-8 py-4 flex justify-end items-center">
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 hover:bg-gray-100 rounded-full relative"
                >
                  <Bell className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {showNotifications && !isLoading && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg z-50">
                    <div className="p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Notifications</h3>
                        <Link 
                          to="/admin/notifications" 
                          className="text-sm text-blue-600 hover:underline"
                          onClick={() => setShowNotifications(false)}
                        >
                          View All
                        </Link>
                      </div>
                      <div className="space-y-4">
                        {notifications.length === 0 ? (
                          <p className="text-gray-500">No notifications</p>
                        ) : (
                          notifications.map((notification) => (
                            <div 
                              key={notification.id} 
                              className={`p-3 rounded-lg ${
                                !notification.read ? 'bg-blue-50' : 'bg-gray-50'
                              } cursor-pointer transition-colors hover:bg-gray-100`}
                              onClick={() => handleNotificationClick(notification)}
                            >
                              <h4 className="font-medium">{notification.title}</h4>
                              <p className="text-sm text-gray-600">{notification.message}</p>
                              <span className="text-xs text-gray-400">
                                {new Date(notification.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="p-8">
            <Routes>
              <Route path="/" element={<DashboardHome />} />
              <Route path="/blog/*" element={<AdminBlog />} />
              <Route path="/blog/new" element={<BlogEditor />} />
              <Route path="/blog/edit/:id" element={<BlogEditor />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/vehicles/new" element={<ManageVehicle />} />
              <Route path="/vehicles/:id" element={<ManageVehicle />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
            </Routes>
          </div>
        </div>
      </div>
    </AdminAuth>
  );
};

export default AdminDashboard;
